import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { openWebCenterDecisionCall } from '@/lib/analytics';
import {
  ArrowRight,
  Calendar,
  AlertTriangle,
  Clock,
  Layers,
  CheckCircle2,
  TrendingDown,
} from 'lucide-react';
import Navigation from '@/components/Navigation';
import SEO from '@/components/SEO';
import Breadcrumbs from '@/components/Breadcrumbs';
import WebCenterEngagementHistory from '@/components/WebCenterEngagementHistory';
import ForwardPathsDiagram from '@/components/diagrams/ForwardPathsDiagram';
import LastUpdated from '@/components/LastUpdated';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { FOUNDER, founderPersonLd } from '@/lib/founder';

const TIMELINE = [
  {
    date: 'December 2026',
    phase: 'Premier Support ends',
    detail:
      'Oracle Fusion Middleware 12c — including all WebCenter products (Content, Imaging, Forms Recognition, Enterprise Capture, Portal, Sites) — exits Premier Support. After this date, Oracle no longer ships new features, certifications, or non-critical updates.',
    severity: 'amber',
  },
  {
    date: 'December 2027',
    phase: 'Extended Support ends',
    detail:
      'Per Oracle\'s Lifetime Support Policy, Fusion Middleware 12c Extended Support concludes — unusually short at just 12 months. After this date, the only Oracle-provided option is Market Driven Support.',
    severity: 'red',
  },
  {
    date: 'Beyond 2027',
    phase: 'Market Driven Support',
    detail:
      'Market Driven Support is yearly-renewable, costs more than Extended Support, and provides limited patching — characterized in industry coverage as "an extraordinarily limited product." This is the option for customers who cannot transition before 2027.',
    severity: 'red',
  },
];

const PATH_OPTIONS = [
  {
    name: 'Path A — Lift-and-shift to OCI Marketplace',
    icon: Layers,
    summary:
      'Move your existing WebCenter implementation to Oracle Cloud Infrastructure using the Oracle WebCenter for OCI Marketplace images (released 24.11.1, available since November 2024). Preserves the WebCenter stack; the infrastructure becomes OCI.',
    timeline: '3-6 months typical',
    outcome:
      'Buys time on the existing WebCenter stack. Customer remains on 12c or upgrades to 14c in place. Same modernization decision recurs at the next WebCenter lifecycle milestone (14c Premier Support ending — estimated 2032+).',
    fit: 'Customers who need to defer the strategic platform decision; customers whose AP automation requirements haven\'t evolved beyond what WebCenter provides; customers with limited modernization budget in the next 6 months.',
  },
  {
    name: 'Path A+ — Upgrade in place to WebCenter 14c',
    icon: TrendingDown,
    summary:
      'Upgrade your existing WebCenter 12c implementation to the 14c release. 14c brings cloud-native support, AI tools, and REST APIs, with 8 years of support per Oracle.',
    timeline: '6-12 months typical',
    outcome:
      'Buys 8 years on the WebCenter stack. AI tooling and REST APIs are improvements but the underlying architecture (Forms Recognition templates, SOA workflow, FIPSA accelerator, ADF customizations) remains.',
    fit: 'Customers with significant existing investment in WebCenter who want the longest possible runway without strategic platform change.',
  },
  {
    name: 'Path B — Modernize off WebCenter to a modern AP automation platform',
    icon: ArrowRight,
    summary:
      'Replace the WebCenter AP stack with EZ Cloud — modern AI-driven AP automation that deploys on Oracle E-Business Suite or Oracle Fusion Cloud ERP using Oracle-supported integration patterns. Preserves the Oracle support posture; transitions the AP automation layer.',
    timeline: '4-9 months typical',
    outcome:
      'Strategic platform decision made once. AP automation moves to a modern AI-native platform. Customer retains EBS or Fusion as system of record. No further WebCenter lifecycle exposure.',
    fit: 'Customers ready to make the strategic decision now; customers whose AP automation requirements have outpaced what WebCenter delivers; customers using the December 2026 forcing function to upgrade the AP operation, not just the infrastructure.',
  },
];

const WHAT_CHANGES = [
  {
    title: 'New features and certifications stop',
    detail:
      'After December 2026, Oracle stops shipping new features, OS certifications, and database certifications for Fusion Middleware 12c. Your WebCenter stack is frozen at whatever the 12.2.1.4 / 12.2.1.19 baseline supports.',
  },
  {
    title: 'Security and patching tighten',
    detail:
      'Extended Support (through December 2027) provides security updates and critical patches but not general bug fixes. Market Driven Support beyond 2027 provides limited patching only.',
  },
  {
    title: 'Audit and compliance exposure',
    detail:
      'Some industries (financial services, healthcare, government) require vendor-supported software in audit scope. Customers in regulated verticals face procurement-driven pressure to transition before audit cycles flag the support gap.',
  },
  {
    title: 'Cost structure shifts',
    detail:
      'Market Driven Support is yearly-renewable and costs more than Extended Support. The total cost of staying on 12c past 2027 climbs each year, with diminishing service levels.',
  },
];

const FAQS = [
  {
    q: 'When exactly does Oracle Fusion Middleware 12c Premier Support end?',
    a: 'December 2026 per Oracle\'s Lifetime Support Policy. Extended Support runs through December 2027 — unusually short at 12 months. After December 2027, the only Oracle-provided option is Market Driven Support, which is yearly-renewable and provides limited patching.',
  },
  {
    q: 'Does this affect WebCenter Content, Imaging, Forms Recognition, and Portal?',
    a: 'Yes. All WebCenter products run on Fusion Middleware 12c and are covered by the same support timeline — WebCenter Content (WCC), WebCenter Imaging (WCI), WebCenter Forms Recognition (WFR), WebCenter Enterprise Capture (WEC), WebCenter Portal (WCP), WebCenter Sites (WCS), and the FIPSA accelerator built on top.',
  },
  {
    q: 'What is Market Driven Support and is it a viable long-term option?',
    a: 'Market Driven Support is Oracle\'s post-Extended-Support program — yearly renewable, costs more than Extended Support, and provides limited patching. Industry coverage characterizes it as a limited product designed to provide transition time, not as a destination. It is appropriate as a 1-3 year bridge while transitioning, not as a long-term strategy.',
  },
  {
    q: 'What is Oracle\'s recommendation?',
    a: 'Oracle\'s public guidance is to initiate planning and execution of upgrades or migration strategies as soon as possible. The Oracle Fusion Middleware 14c release is positioned as the forward path for customers staying on the WebCenter stack; OCI Marketplace images for 12c (release 24.11.1+) support the lift-and-shift transition.',
  },
  {
    q: 'Is upgrading from 12c to 14c the same as modernizing?',
    a: 'No. A 12c → 14c upgrade preserves the WebCenter architecture and customizations — Forms Recognition templates, SOA composites, WebLogic configuration, FIPSA accelerator, ADF customizations. Modernization to a different AP automation platform replaces those layers with modern equivalents. Both are valid forward paths; they\'re different strategic choices.',
  },
  {
    q: 'What does this mean for our existing WebCenter AP implementation?',
    a: 'Every WebCenter AP customer has a decision to make before December 2026. The three primary options are Path A (lift-and-shift to OCI Marketplace), Path A+ (12c → 14c upgrade), or Path B (modernize off WebCenter to a modern AP automation platform). EZ Cloud\'s WebCenter AP Modernization Assessment helps you make that decision with a structured read of your existing implementation.',
  },
];

const WebCenter12cEndOfSupport = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  const personSchema = { '@context': 'https://schema.org', ...founderPersonLd };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'TechArticle',
    headline: 'Oracle Fusion Middleware 12c End of Premier Support — The WebCenter Decision',
    description:
      'Oracle Fusion Middleware 12c — including all WebCenter products — exits Premier Support in December 2026. Extended Support ends December 2027. What this means for your WebCenter AP implementation and the December 2026 modernization decision.',
    author: { '@id': 'https://ezcloud.ai/about#andrew-blackman' },
    publisher: { '@id': 'https://ezcloud.ai/#organization' },
    datePublished: '2026-06-29',
    dateModified: '2026-06-29',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': 'https://ezcloud.ai/solutions/webcenter-12c-end-of-support-decision',
    },
    about: [
      { '@type': 'Thing', name: 'Oracle Fusion Middleware 12c' },
      { '@type': 'Thing', name: 'Oracle WebCenter Content' },
      { '@type': 'Thing', name: 'Oracle WebCenter Imaging' },
      { '@type': 'Thing', name: 'Oracle WebCenter Forms Recognition' },
      { '@type': 'Thing', name: 'Oracle Lifetime Support Policy' },
    ],
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Oracle WebCenter 12c End of Support — The December 2026 Decision | EZ Cloud"
        description="Oracle Fusion Middleware 12c — including all WebCenter products (Content, Imaging, Forms Recognition, Enterprise Capture) — Premier Support ends December 2026. Extended Support ends December 2027. What this means for your WebCenter AP implementation."
        canonical="https://ezcloud.ai/solutions/webcenter-12c-end-of-support-decision"
        breadcrumbs={[
          { name: 'Home', url: 'https://ezcloud.ai/' },
          { name: 'Solutions', url: 'https://ezcloud.ai/solutions/modernize-oracle-webcenter' },
          {
            name: 'WebCenter 12c End of Support',
            url: 'https://ezcloud.ai/solutions/webcenter-12c-end-of-support-decision',
          },
        ]}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(personSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <Navigation />
      <Breadcrumbs />
      <LastUpdated date="2026-06-29" />

      <main>
        {/* Hero */}
        <section className="bg-hero-gradient text-white pt-16 pb-20 relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 left-0 w-1/2 h-full bg-gradient-to-br from-primary/20 to-transparent" />
            <div className="absolute top-20 right-20 w-96 h-96 bg-accent/10 rounded-full blur-3xl" />
          </div>
          <div className="container mx-auto px-4 max-w-5xl relative">
            <div className="flex items-center gap-2 text-accent text-sm font-semibold uppercase tracking-wide mb-4">
              <Clock className="w-4 h-4" aria-hidden />
              Decision Window · The December 2026 Cliff
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
              Oracle Fusion Middleware 12c Premier Support ends
              <span className="block text-accent mt-3">December 2026.</span>
            </h1>
            <p className="text-xl md:text-2xl text-white/80 max-w-3xl mb-8">
              Every Oracle WebCenter AP customer in the world has a decision to make in the next
              6 months. This page is the framework for making it well.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                size="lg"
                variant="accent"
                className="font-semibold"
                onClick={() => openWebCenterDecisionCall('webcenter-page')}
              >
                <Calendar className="mr-2 w-5 h-5" aria-hidden />
                Book a WebCenter Decision Call
                <ArrowRight className="ml-2 w-5 h-5" aria-hidden />
              </Button>
              <Button size="lg" variant="outline-on-dark" asChild>
                <Link to="/solutions/modernize-oracle-webcenter">
                  See the modernization path
                </Link>
              </Button>
            </div>
          </div>
        </section>

        {/* TL;DR / definitional summary */}
        <section className="py-14 bg-white border-b border-gray-100">
          <div className="container mx-auto px-4 max-w-4xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3">
              What this is
            </p>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-5 leading-tight">
              The Oracle Fusion Middleware 12c support cliff, explained
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>
                <strong className="text-gray-900">
                  Oracle Fusion Middleware 12c — the platform underneath every Oracle WebCenter
                  product (Content, Imaging, Forms Recognition, Enterprise Capture, Portal, Sites)
                  — exits Premier Support in December 2026.
                </strong>{' '}
                Extended Support concludes December 2027, unusually short at 12 months. After 2027,
                the only Oracle-provided option is Market Driven Support — yearly-renewable, costs
                more than Extended Support, and provides limited patching.
              </p>
              <p>
                Every WebCenter AP customer faces three forward paths: lift-and-shift the existing
                implementation to Oracle Cloud Infrastructure via the Oracle WebCenter for OCI
                Marketplace images (Path A), upgrade in place to WebCenter 14c (Path A+), or
                modernize off WebCenter to a modern AP automation platform that deploys on Oracle
                E-Business Suite or Oracle Fusion Cloud ERP (Path B).
              </p>
              <p>
                EZ Cloud is the only WebCenter AP modernization partner published on the{' '}
                <a
                  href="https://docs.oracle.com/en/solutions/ez-cloud-on-oci/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary font-semibold underline decoration-primary/40 underline-offset-2 hover:decoration-accent hover:text-accent"
                >
                  Oracle Reference Architecture for OCI
                </a>
                . Founder-led by{' '}
                <Link
                  to="/about#andrew-blackman"
                  className="text-primary font-semibold hover:text-accent transition-colors"
                >
                  Andrew Blackman
                </Link>
                , a 25-year Oracle WebCenter AP specialist.
              </p>
            </div>
          </div>
        </section>

        {/* Timeline */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              The timeline
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              Oracle Fusion Middleware 12c support phases
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              The dates that define the WebCenter AP decision window. Per Oracle Lifetime Support
              Policy. Every FMW component's dates are consolidated in our{' '}
              <Link
                to="/resources/oracle-fusion-middleware-support-dates"
                className="font-semibold text-primary hover:text-accent transition-colors"
              >
                support-dates reference table
              </Link>
              , and the whole-stack view lives in the{' '}
              <Link
                to="/solutions/oracle-fusion-middleware-12c-end-of-support"
                className="font-semibold text-primary hover:text-accent transition-colors"
              >
                Fusion Middleware 12c end-of-support guide
              </Link>
              .
            </p>
            <div className="space-y-5">
              {TIMELINE.map((t) => (
                <Card
                  key={t.date}
                  className={`p-7 bg-white border shadow-sm ${
                    t.severity === 'red' ? 'border-red-200' : 'border-amber-200'
                  }`}
                >
                  <div className="flex flex-col sm:flex-row gap-5 items-start">
                    <div
                      className={`flex-shrink-0 px-4 py-2 rounded-lg font-bold text-sm uppercase tracking-wide ${
                        t.severity === 'red'
                          ? 'bg-red-50 text-red-700 border border-red-200'
                          : 'bg-amber-50 text-amber-700 border border-amber-200'
                      }`}
                    >
                      {t.date}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{t.phase}</h3>
                      <p className="text-gray-700 leading-relaxed">{t.detail}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* What changes — two-column editorial layout (deliberate rhythm break
            from the centered card-grid grammar used elsewhere on the page) */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-6xl">
            <div className="grid lg:grid-cols-5 gap-10 lg:gap-16">
              <div className="lg:col-span-2">
                <div className="lg:sticky lg:top-28">
                  <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3">
                    What changes for your WebCenter implementation
                  </p>
                  <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-5 leading-tight">
                    Four shifts every WebCenter AP customer should plan for
                  </h2>
                  <p className="text-gray-600 leading-relaxed">
                    None of these arrive as an outage. Each arrives as a slow tightening —
                    certifications that stop, patches that stop, auditors who start asking, and a
                    support bill that changes shape. Planning for them early is what keeps the
                    December 2026 boundary a decision instead of an emergency.
                  </p>
                </div>
              </div>
              <div className="lg:col-span-3 space-y-4">
                {WHAT_CHANGES.map((c, i) => (
                  <div key={c.title} className="flex items-start gap-5 border-b border-gray-200 pb-4 last:border-b-0">
                    <span className="text-3xl font-bold text-accent/40 leading-none flex-shrink-0 w-10">
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    <div>
                      <h3 className="text-gray-900 font-semibold mb-1">{c.title}</h3>
                      <p className="text-sm text-gray-600 leading-relaxed">{c.detail}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* The decision, drawn */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              The decision, drawn
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-10">
              One diagram of the December 2026 decision
            </h2>
            <ForwardPathsDiagram />
          </div>
        </section>

        {/* Path options */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-6xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              The three forward paths
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              Which path is right for your WebCenter AP implementation?
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              Every WebCenter AP customer chooses one of these three paths in response to the
              Fusion Middleware 12c support cliff. The right answer depends on your stack
              complexity, your AP requirements, and your budget timing.
            </p>
            <div className="space-y-6">
              {PATH_OPTIONS.map(({ name, icon: Icon, summary, timeline, outcome, fit }) => (
                <Card key={name} className="p-7 md:p-9 bg-white border border-gray-200 shadow-sm">
                  <div className="flex items-start gap-4 mb-5">
                    <div className="w-12 h-12 rounded-lg bg-accent/20 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-6 h-6 text-accent" aria-hidden />
                    </div>
                    <h3 className="text-xl md:text-2xl font-bold text-gray-900 leading-tight">
                      {name}
                    </h3>
                  </div>
                  <div className="grid md:grid-cols-2 gap-6 text-sm">
                    <div className="space-y-4">
                      <div>
                        <p className="text-xs font-bold uppercase tracking-wide text-accent mb-2">
                          Summary
                        </p>
                        <p className="text-gray-700 leading-relaxed">{summary}</p>
                      </div>
                      <div>
                        <p className="text-xs font-bold uppercase tracking-wide text-accent mb-2">
                          Typical timeline
                        </p>
                        <p className="text-gray-700 leading-relaxed">{timeline}</p>
                      </div>
                    </div>
                    <div className="space-y-4">
                      <div>
                        <p className="text-xs font-bold uppercase tracking-wide text-accent mb-2">
                          Outcome
                        </p>
                        <p className="text-gray-700 leading-relaxed">{outcome}</p>
                      </div>
                      <div>
                        <p className="text-xs font-bold uppercase tracking-wide text-accent mb-2">
                          Best fit
                        </p>
                        <p className="text-gray-700 leading-relaxed">{fit}</p>
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Decision Guide CTA */}
        <section className="py-12 bg-accent/5 border-y border-accent/30">
          <div className="container mx-auto px-4 max-w-4xl text-center">
            <p className="text-xs font-bold uppercase tracking-wide text-accent mb-2">
              Free resource
            </p>
            <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
              The Oracle WebCenter AP December 2026 Decision Guide
            </h3>
            <p className="text-gray-700 leading-relaxed mb-6 max-w-2xl mx-auto">
              The structured guide to the four forward paths, stack-specific considerations,
              eight diagnostic questions, and vendor evaluation checklist. Get the printable PDF.
            </p>
            <Link
              to="/resources/webcenter-decision-guide"
              className="inline-flex items-center font-semibold text-white bg-accent hover:bg-accent/90 transition-colors px-6 py-3 rounded-lg"
            >
              Read the Decision Guide
              <ArrowRight className="w-4 h-4 ml-2" aria-hidden />
            </Link>
          </div>
        </section>

        {/* Cross-link to hub */}
        <section className="py-12 bg-white">
          <div className="container mx-auto px-4 max-w-4xl">
            <Card className="p-8 md:p-10 bg-accent/5 border border-accent/30 shadow-sm">
              <p className="text-xs font-bold uppercase tracking-wide text-accent mb-3">
                The full modernization path
              </p>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                See how EZ Cloud reads your WebCenter implementation directly
              </h3>
              <p className="text-gray-700 leading-relaxed mb-5">
                The Modernize Oracle WebCenter AP page covers the full stack — Forms Recognition,
                SOA, WebLogic, FIPSA, ADF — across 11g, 12c, and 14c. Deploys on Oracle E-Business
                Suite or Oracle Fusion Cloud ERP. Includes the productized WebCenter AP
                Modernization Assessment plus engagement tiers for customers choosing every forward
                path.
              </p>
              <Link
                to="/solutions/modernize-oracle-webcenter"
                className="inline-flex items-center font-semibold text-primary hover:text-accent transition-colors"
              >
                See the WebCenter AP modernization path
                <ArrowRight className="w-4 h-4 ml-1" aria-hidden />
              </Link>
            </Card>
          </div>
        </section>

        <WebCenterEngagementHistory />

        {/* FAQ */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              Common questions
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-10">
              Direct answers on the December 2026 decision
            </h2>
            <Accordion type="single" collapsible className="w-full">
              {FAQS.map((f, i) => (
                <AccordionItem key={f.q} value={`item-${i}`}>
                  <AccordionTrigger className="text-left text-gray-900">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-gray-600">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-hero-gradient text-white">
          <div className="container mx-auto px-4 max-w-3xl text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              The decision window is open. Let's talk through your path.
            </h2>
            <p className="text-xl text-white/80 mb-8 leading-relaxed">
              A 30-minute call with {FOUNDER.name}, founder of EZ Cloud and a 25-year Oracle
              WebCenter AP specialist. We'll walk through your current WebCenter footprint and the
              three paths in the context of your situation.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                size="lg"
                variant="accent"
                className="font-semibold"
                onClick={() => openWebCenterDecisionCall('webcenter-page')}
              >
                <Calendar className="mr-2 w-5 h-5" aria-hidden />
                Book a WebCenter Decision Call
                <ArrowRight className="ml-2 w-5 h-5" aria-hidden />
              </Button>
              <Button size="lg" variant="outline-on-dark" asChild>
                <Link to="/solutions/modernize-oracle-webcenter">
                  See the modernization path
                </Link>
              </Button>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default WebCenter12cEndOfSupport;
